import React, { useState, useEffect, useCallback } from 'react';
import {
  MdOutlineThumbUp,
  MdPlayArrow,
  MdShuffle,
  MdOutlineDelete,
  MdCheckCircle,
} from 'react-icons/md';
import Toast from './Toast';
import { getActiveAccount, PRIMARY_VALID_TOKEN } from './utils/accountUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './LikedVideosPage.css';

const DEFAULT_THUMBNAIL = normalizeMediaUrl('/drive/video_thumbnails/thumbnail-157.jpeg') || `${process.env.PUBLIC_URL || ''}/img/banner.png`;

export default function LikedVideosPage({ userProfile, onNavigate }) {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', icon: null });

  const showToast = (message, icon = null) => {
    setToast({ isOpen: false, message: '', icon: null });
    setTimeout(() => {
      setToast({ isOpen: true, message, icon });
    }, 50);
  };

  const fetchLikedVideos = useCallback(async () => {
    setIsLoading(true);
    let backendVideos = [];

    // 1. Fetch from backend API
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (token) {
        const res = await fetch(`${API_BASE_URL}/api/v1/liked-videos/users/me`, {
          headers: {
            devicetype: '0',
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            request_type: 'USER',
            authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
            const json = await res.json();
            const list = json?.data?.likedVideos || [];
            backendVideos = list.map((v) => ({
              id: String(v.video_id || v.id),
              videoUrlId: v.video_url_id || 'HyRyzOCHONj',
              title: v.title || 'SpeedoTube Video',
              channelName: v.channel_name || 'Speedo Creator',
              thumbnailUrl: normalizeMediaUrl(v.thumbnail_url) || DEFAULT_THUMBNAIL,
              duration: typeof v.duration === 'number' ? `${Math.floor(v.duration / 60)}:${(v.duration % 60).toString().padStart(2, '0')}` : (v.duration || '2:53'),
              views: typeof v.views === 'number' ? `${v.views.toLocaleString()} views` : (v.views || '1 view'),
            }));
          }
        }
      } catch (e) {
      console.warn('Failed to load backend liked videos:', e);
    }

    // 2. Merge with locally saved liked videos
    try {
      const savedLocal = localStorage.getItem('speedotube_liked_videos');
      const localList = savedLocal ? JSON.parse(savedLocal) : [];

      const map = new Map();
      backendVideos.forEach((v) => map.set(String(v.id), v));
      localList.forEach((v) => {
        if (!map.has(String(v.id))) {
          map.set(String(v.id), {
            id: String(v.id),
            videoUrlId: v.videoUrlId || 'HyRyzOCHONj',
            title: v.title || 'SpeedoTube Video',
            channelName: v.channelName || 'Speedo Creator',
            thumbnailUrl: v.thumbnailUrl || DEFAULT_THUMBNAIL,
            duration: v.duration || '2:53',
            views: v.views || '1 view',
          });
        }
      });

      setVideos(Array.from(map.values()));
    } catch (e) {
      console.warn('Failed to merge local liked videos:', e);
      setVideos(backendVideos);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLikedVideos();

    const handleUpdated = () => fetchLikedVideos();
    window.addEventListener('speedotube_liked_videos_updated', handleUpdated);
    return () => window.removeEventListener('speedotube_liked_videos_updated', handleUpdated);
  }, [fetchLikedVideos]);

  // Remove video from Liked videos (unliking)
  const handleRemove = async (e, videoId) => {
    e.stopPropagation();

    // 1. Optimistic UI update
    setVideos((prev) => prev.filter((v) => v.id !== String(videoId)));

    // 2. Local storage update
    try {
      const savedLocal = localStorage.getItem('speedotube_liked_videos');
      const list = savedLocal ? JSON.parse(savedLocal) : [];
      const filtered = list.filter((v) => String(v.id) !== String(videoId));
      localStorage.setItem('speedotube_liked_videos', JSON.stringify(filtered));
      localStorage.removeItem(`speedotube_liked_${videoId}`);
      window.dispatchEvent(new Event('speedotube_liked_videos_updated'));
      window.dispatchEvent(new Event('speedotube_playlist_created'));
    } catch (err) {
      console.warn('Local liked video removal error:', err);
    }

    showToast('Removed from Liked videos');

    // 3. Backend API call to toggle like off
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (token) {
        await fetch(`${API_BASE_URL}/api/v1/videos/${videoId}/like`, {
          method: 'POST',
          headers: {
            devicetype: '0',
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            request_type: 'USER',
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ like_dislike: 'like' }),
        });
      }
    } catch (err) {
      console.warn('Backend unlike error:', err);
    }
  };

  const handlePlayVideo = (videoUrlId) => {
    if (onNavigate) {
      onNavigate(`/watch?v=${videoUrlId}`);
    }
  };

  const currentAcc = userProfile || getActiveAccount();

  return (
    <div className="yt-watch-later-page">
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        icon={toast.icon}
        onClose={() => setToast({ isOpen: false, message: '', icon: null })}
      />

      {/* Left Sidebar / Hero Banner */}
      <div className="yt-watch-later-sidebar">
        <div className="yt-wl-banner-card">
          <div className="yt-wl-thumb-preview">
            {videos.length > 0 ? (
              <>
                <img src={videos[0].thumbnailUrl} alt="Liked Videos Preview" className="yt-wl-thumb-img" />
                <div className="yt-wl-thumb-overlay">
                  <MdOutlineThumbUp size={32} />
                  <span>Auto playlist</span>
                </div>
              </>
            ) : (
              <div className="yt-wl-thumb-placeholder">
                <MdOutlineThumbUp size={52} className="yt-wl-placeholder-icon" />
              </div>
            )}
          </div>

          <div className="yt-wl-details">
            <h1 className="yt-wl-title">Liked videos</h1>
            <p className="yt-wl-owner">{currentAcc?.name || 'User'}</p>
            <div className="yt-wl-stats">
              <span>{videos.length} {videos.length === 1 ? 'video' : 'videos'}</span>
              <span>•</span>
              <span>Auto playlist</span>
            </div>

            <div className="yt-wl-privacy-badge">
              <span>Auto playlist</span>
            </div>

            {videos.length > 0 && (
              <div className="yt-wl-action-buttons">
                <button
                  className="yt-wl-btn primary"
                  onClick={() => handlePlayVideo(videos[0].videoUrlId)}
                >
                  <MdPlayArrow size={22} />
                  <span>Play all</span>
                </button>
                <button
                  className="yt-wl-btn secondary"
                  onClick={() => {
                    const randomIdx = Math.floor(Math.random() * videos.length);
                    handlePlayVideo(videos[randomIdx].videoUrlId);
                  }}
                >
                  <MdShuffle size={18} />
                  <span>Shuffle</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right List of Videos */}
      <div className="yt-watch-later-main">
        {isLoading ? (
          <div className="yt-wl-loading">Loading liked videos...</div>
        ) : videos.length === 0 ? (
          <div className="yt-wl-empty-state">
            <div className="yt-wl-empty-icon">
              <MdOutlineThumbUp size={64} />
            </div>
            <h2>No liked videos yet</h2>
            <p>Videos that you like will show up right here.</p>
            <button className="yt-wl-browse-btn" onClick={() => onNavigate && onNavigate('/')}>
              Browse Videos
            </button>
          </div>
        ) : (
          <div className="yt-wl-video-list">
            {videos.map((vid, idx) => (
              <div
                key={vid.id}
                className="yt-wl-item-row yt-wl-video-row"
                onClick={() => handlePlayVideo(vid.videoUrlId)}
              >
                <span className="yt-wl-item-index">{idx + 1}</span>

                <div className="yt-wl-item-thumb-wrap">
                  <img src={vid.thumbnailUrl} alt={vid.title} className="yt-wl-item-thumb" />
                  <span className="yt-wl-item-duration">{vid.duration}</span>
                </div>

                <div className="yt-wl-item-info">
                  <h3 className="yt-wl-item-title">{vid.title}</h3>
                  <div className="yt-wl-item-meta">
                    <span className="yt-wl-item-channel">{vid.channelName}</span>
                    <span className="yt-wl-item-views">• {vid.views}</span>
                  </div>
                </div>

                <button
                  className="yt-wl-item-remove-btn"
                  title="Remove from Liked videos"
                  aria-label="Remove from Liked videos"
                  onClick={(e) => handleRemove(e, vid.id)}
                >
                  <MdOutlineDelete size={20} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
